import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  getShiftAssignments,
  deleteShiftAssignment,
} from "../../apis/shiftAssignment";
import AssignShiftModal from "./AssignShiftModal";
import Modal from "../../components/Modal";
import CommonPagination from "../../components/CommanPagination";
import { LanguageContext } from "../../providers/LanguageProvider";
import { formatTimeToAmPm } from "../../utils/DateUtil";

const ShiftAssignmentsList = () => {
  const navigate = useNavigate();
  const { translate: t } = useContext(LanguageContext);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [meta, setMeta] = useState<{
    currentPage: number;
    perPage: number;
    total: number;
  }>({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [isAssignModalOpen, setAssignModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    userName: "",
    shiftName: "",
    date: "",
  });

  const fetchAssignments = useCallback((params: any = { page: 1, per_page: 10 }) => {
    setLoading(true);
    getShiftAssignments(params)
      .then((res: any) => {
        const data = res.data?.data ?? res.data ?? [];
        const pagination = res.data?.meta ?? res.meta ?? {};
        setAssignments(Array.isArray(data) ? data : []);
        setMeta({
          currentPage: pagination.currentPage ?? pagination.current_page ?? 1,
          perPage: pagination.perPage ?? pagination.per_page ?? 10,
          total: pagination.total ?? 0,
        });
      })
      .catch(() => {
        setAssignments([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchWithFilters = useCallback(
    (params: any = {}) => {
      const baseParams: any = {
        page: params.page ?? 1,
        per_page: params.per_page ?? 10,
      };
      if (filters.userName.trim()) baseParams.user_name = filters.userName.trim();
      if (filters.shiftName.trim()) baseParams.shift_name = filters.shiftName.trim();
      if (filters.date) baseParams.date = filters.date;
      fetchAssignments({ ...baseParams, ...params });
    },
    [filters, fetchAssignments]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchWithFilters({ page: 1 });
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.userName, filters.shiftName, filters.date, fetchWithFilters]);

  const onPaginationChange = (page: number, pageSize?: number) => {
    fetchWithFilters({ page, per_page: pageSize ?? 10 });
  };

  const handleFilterChange = (key: keyof typeof filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleAssignSuccess = () => {
    fetchWithFilters({ page: meta.currentPage, per_page: meta.perPage });
    setAssignModalOpen(false);
  };

  const handleDeleteAssignment = (id: number) => {
    if (window.confirm("Remove this assignment?")) {
      deleteShiftAssignment(id).then(() => {
        fetchWithFilters({ page: meta.currentPage, per_page: meta.perPage });
      });
    }
  };

  const getUserName = (a: any) => {
    const cu = a.clinicUser;
    if (cu?.user) {
      const u = cu.user;
      return `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.name || u.email || "-";
    }
    if (cu?.name) return cu.name;
    const u = a.user ?? a;
    if (!u) return "-";
    return `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.name || u.email || "-";
  };

  const getShiftName = (a: any) => {
    const s = a.shiftDefinition;
    if (!s) return "-";
    return s.name || "-";
  };

  const getShiftTime = (a: any) => {
    const s = a.shiftDefinition;
    if (!s) return "";
    const st = s.startTime ?? "";
    const et = s.endTime ?? "";
    if (!st || !et) return "";
    return `${formatTimeToAmPm(st)} - ${formatTimeToAmPm(et)}`;
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/duty-roster")}
            className="text-sm font-medium text-primary hover:text-hover mb-2"
          >
            ← Back to Duty Roster
          </button>
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Shift Assignments
          </h1>
          <p className="mt-1 text-sm text-gray-700">
            View and manage shift assignments.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAssignModalOpen(true)}
          className="shrink-0 px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          Assign Shift
        </button>
      </div>

      <div className="mt-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                User
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Shift
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Date
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                {t("actions")}
              </th>
            </tr>
            <tr className="border-t border-gray-200">
              <th className="py-2 pl-4 pr-3 sm:pl-6">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Filter by user..."
                    value={filters.userName}
                    onChange={(e) => handleFilterChange("userName", e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm pr-8 sm:text-sm focus:border-primary focus:ring-primary"
                  />
                  {filters.userName && (
                    <button
                      type="button"
                      onClick={() => handleFilterChange("userName", "")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </th>
              <th className="px-3 py-2">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Filter by shift..."
                    value={filters.shiftName}
                    onChange={(e) => handleFilterChange("shiftName", e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm pr-8 sm:text-sm focus:border-primary focus:ring-primary"
                  />
                  {filters.shiftName && (
                    <button
                      type="button"
                      onClick={() => handleFilterChange("shiftName", "")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </th>
              <th className="px-3 py-2">
                <div className="relative">
                  <input
                    type="date"
                    placeholder="Filter by date"
                    value={filters.date}
                    onChange={(e) => handleFilterChange("date", e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm pr-8 sm:text-sm focus:border-primary focus:ring-primary"
                  />
                  {filters.date && (
                    <button
                      type="button"
                      onClick={() => handleFilterChange("date", "")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={4} className="py-8 text-center text-sm text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : assignments.length > 0 ? (
              assignments.map((a) => (
                <tr key={a.id}>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    {getUserName(a)}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {getShiftName(a)} {getShiftTime(a) && `(${getShiftTime(a)})`}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {a.date || "-"}
                  </td>
                  <td className="px-3 py-4">
                    <button
                      onClick={() => handleDeleteAssignment(a.id)}
                      className="text-sm font-semibold text-red-600 hover:text-red-500"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="py-8 text-center text-sm text-gray-500">
                  No assignments yet. Assign a shift to a user.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <CommonPagination meta={meta} onChange={onPaginationChange} />
      </div>

      {isAssignModalOpen && (
        <Modal
          title="Assign Shift"
          onClose={() => setAssignModalOpen(false)}
          modalContent={
            <AssignShiftModal
              onClose={() => setAssignModalOpen(false)}
              onAssigned={handleAssignSuccess}
            />
          }
        />
      )}
    </div>
  );
};

export default ShiftAssignmentsList;
